if (!customElements.get('me-agentur-inventory')) {
  class MeAgenturInventory extends HTMLElement {
    connectedCallback() {
      this.productInfo = this.closest('.product-info');
      if (!this.productInfo) return;

      this.records = new Map();
      this.currentVariantId = this.normalizeVariantId(this.dataset.variantId);
      const configuredMaxAge = Number(this.dataset.maxAgeSeconds);
      this.maxAgeMs = Number.isFinite(configuredMaxAge) && configuredMaxAge > 0
        ? configuredMaxAge * 1000 : 120000;
      this.readEmbeddedInventory();

      this.onVariantChange = (event) => {
        this.currentVariantId = this.normalizeVariantId(event.detail?.variant?.id);
        this.dataset.variantId = this.currentVariantId;
        this.renderInventory();
        this.requestInventory();
      };
      this.onResume = () => {
        this.renderInventory();
        this.requestInventory();
      };
      this.onVisibilityChange = () => {
        if (!document.hidden) this.onResume();
      };
      this.variantPicker = this.productInfo.querySelector('variant-picker');
      this.variantPicker?.addEventListener('on:variant:change', this.onVariantChange);
      window.addEventListener('pageshow', this.onResume);
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      this.observer = new MutationObserver(() => {
        const shouldReplace = !this.hidden;
        if (this.productInfo.classList.contains('me-agentur-own-stock') !== shouldReplace) {
          this.productInfo.classList.toggle('me-agentur-own-stock', shouldReplace);
        }
      });
      this.observer.observe(this.productInfo, { childList: true, subtree: true });
      this.renderInventory();
      this.requestInventory();
      this.refreshTimer = setInterval(() => {
        if (!document.hidden) this.onResume();
      }, 45000);
    }

    disconnectedCallback() {
      clearTimeout(this.expiryTimer);
      clearInterval(this.refreshTimer);
      this.observer?.disconnect();
      this.variantPicker?.removeEventListener('on:variant:change', this.onVariantChange);
      window.removeEventListener('pageshow', this.onResume);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.productInfo?.classList.remove('me-agentur-own-stock');
    }

    get selectedVariantId() {
      return this.currentVariantId;
    }

    requestInventory() {
      if (!this.currentVariantId) return;
      this.dispatchEvent(new CustomEvent('me:agentur:inventory-request', {
        detail: { variantId: this.currentVariantId, component: this }, bubbles: true
      }));
    }

    normalizeVariantId(value) {
      const id = String(value ?? '').split('/').pop();
      return /^\d+$/.test(id) ? id : '';
    }

    parseTimestamp(value) {
      if (typeof value === 'string' && /^\d+$/.test(value)) value = Number(value);
      if (typeof value === 'number' && Number.isFinite(value)) {
        return value < 100000000000 ? value * 1000 : value;
      }
      return typeof value === 'string' && value ? Date.parse(value) : NaN;
    }

    readEmbeddedInventory() {
      try {
        const records = JSON.parse(this.querySelector('[data-me-agentur-inventory]')?.textContent || '[]');
        if (!Array.isArray(records)) return;
        records.forEach((record) => {
          const id = this.normalizeVariantId(record.id);
          const locationId = String(record.location_id ?? '').split('/').pop();
          const sourceUpdatedAt = this.parseTimestamp(record.source_updated_at);
          const now = Date.now();
          if (!id || locationId !== this.dataset.locationId
            || !Number.isSafeInteger(record.available)
            || !Number.isSafeInteger(record.total_available)
            || !Number.isSafeInteger(record.current_total_available)
            || record.total_available !== record.current_total_available
            || !Number.isFinite(sourceUpdatedAt) || sourceUpdatedAt <= 0
            || sourceUpdatedAt > now + 30000) return;
          this.records.set(id, {
            quantity: record.available,
            verified: true,
            checkedAt: now
          });
        });
      } catch {
        this.records.clear();
      }
    }

    applyInventory(variantId, quantity, verified, checkedAt = Date.now()) {
      const id = this.normalizeVariantId(variantId);
      if (!id || !this.records) return;
      this.records.set(id, {
        quantity,
        verified: verified === true,
        checkedAt: this.parseTimestamp(checkedAt)
      });
      if (id === this.currentVariantId) this.renderInventory();
    }

    renderInventory() {
      clearTimeout(this.expiryTimer);
      const record = this.records.get(this.currentVariantId);
      const now = Date.now();
      const age = record ? now - record.checkedAt : NaN;
      const positive = record?.verified === true
        && Number.isSafeInteger(record.quantity) && record.quantity > 0
        && Number.isFinite(age) && age >= -30000 && age < this.maxAgeMs;
      this.hidden = !positive;
      this.productInfo.classList.toggle('me-agentur-own-stock', positive);
      this.dataset.inventoryState = positive ? 'own-stock' : 'fallback';
      if (positive) {
        this.expiryTimer = setTimeout(() => this.renderInventory(), this.maxAgeMs - age + 1);
      }
    }
  }

  customElements.define('me-agentur-inventory', MeAgenturInventory);
}
